import React, { useState } from 'react';
import { X, AlertCircle, Send } from 'lucide-react';

/**
 * RejectModal
 *
 * Modal for rejecting a submitted report with a reason.
 * The reason will be sent to the contractor as feedback.
 */
export default function RejectModal({
  instance,
  onClose,
  onReject
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) return;

    setSubmitting(true);
    try {
      await onReject(reason);
    } finally {
      setSubmitting(false);
    }
  };

  const job = instance?.job;
  const contractor = instance?.contractor;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="bg-background w-full max-w-md rounded-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-text-primary">Reject Report</h2>
              <p className="text-text-secondary text-sm">Send back for revision</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-text-secondary hover:text-text-primary hover:bg-surface-light rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Job & Contractor Info */}
          <div className="mb-4 p-3 bg-surface rounded-lg">
            <p className="text-text-primary font-medium">{job?.job_name}</p>
            <p className="text-text-secondary text-sm">Submitted by: {contractor?.full_name}</p>
          </div>

          {/* Rejection Reason */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Rejection Reason <span className="text-red-400">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain what needs to be corrected or improved..."
              rows={4}
              className="w-full px-4 py-3 bg-surface border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:border-red-500 resize-none"
              required
            />
            <p className="mt-2 text-xs text-text-secondary">
              This feedback will be visible to the contractor.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-border hover:bg-surface-light text-text-primary rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!reason.trim() || submitting}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-600/50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
            >
              <Send size={18} />
              {submitting ? 'Rejecting...' : 'Reject & Send Feedback'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
